import React, { useEffect, useState, useRef } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import styled from 'styled-components/native';
import { FlexBlockWrapped, FlexElement, StrongText, MetricStackTitle, ShinyBlockOuter, ShinyBody, ShinyHeading, FlexBlock, PageWrapper, Heading4, BasicText } from '../Styles';
import { MetricStack } from '../TestingLayout';
import { Tabular, TabularColumn } from '../core/Tabular';
import _ from 'lodash';
import { ColorBlock } from '../Things';
import BarChart from '../core/BarChart';
import { COLORS } from '../../WasteTheme';
import Metrics, { Metric } from '../core/Metrics';
import * as scale from 'd3-scale';
import moment from 'moment';
import Util from '../core/Util';

const styles = StyleSheet.create({
    containerStyle: {
        flexDirection: "column",
        flexBasis: "49%",
        justifyContent: "space-evenly",
        borderBottomLeftRadius: 5,
        borderBottomRightRadius: 5,
        marginTop: 5,
        marginRight: 5,
        marginBottom: 10,
        marginLeft: 5
    }
});

const Diversion = ({portalData}) => {
    const [data, setData] = useState([]);
    const [hideRankingTable, setHideRankingTable] = useState(false);
    const pageSize = useRef(10);

    useEffect(()=>{
        const tempData = {};
        let theRank = 1;
        let displayedRank = 1;
        let previousValue = 0;
        const shouldHideRanking = _.uniq(_.map(portalData.umbrellaSites, "name")).length <= 1;
        const dataKey = shouldHideRanking? "sites" : "umbrellaSites";

        _.forEach(portalData[dataKey], (site, idx)=>{
            if(tempData[site.site_id] == undefined){
                tempData[site.site_id] = site;
            } else {
                tempData[site.site_id].metric_tonnes_diverted += site.metric_tonnes_diverted;
                tempData[site.site_id].metric_tonnes_waste += site.metric_tonnes_waste;
            }
        });

        const newData = _.reverse(_.sortBy(_.map(_.values(tempData), (site, idx)=> {
            previousValue = site.metric_tonnes_diverted;
            site.diversion_percent = (site.metric_tonnes_diverted * 100) / (site.metric_tonnes_diverted + site.metric_tonnes_waste);

            if (isNaN(site.percent_diverted)) {
                site.percent_diverted = 0;
            }

            if (isNaN(site.diversion_percent)) {
                site.diversion_percent = 0;
            }

            return site;
        }), "diversion_percent"));

        _.forEach(newData, (site)=> {
            if(site.metric_tonnes_diverted != previousValue){
                displayedRank = theRank;
            }
            site.rank = displayedRank;

            theRank++;
        });

        setHideRankingTable(shouldHideRanking);
        setData(newData);
    },[portalData]);

    if(hideRankingTable && data.length > 0){
        const diversionPercent = data[0].diversion_percent;
        const landfillPercent = 100 - diversionPercent;

        return <View style={styles.containerStyle}>
            <MetricStack key={"single-site-diversion-data-landfill"} flexBasis="32%" style={{margin: 0, flexGrow: 0}}>
                <Metric key={"landfill"} title={"Landfill"} value={Util.roundNice(data[0].metric_tonnes_waste, 2) + " MT"} icon="wsc:garbage" subtitle={Util.roundNice(landfillPercent, 2) + "%"}/>
            </MetricStack>
            <MetricStack key={"single-site-diversion-data-diversion"} flexBasis="32%" style={{margin: 0, flexGrow: 0}}>
                <Metric key={"diversion"} title={"Diverted"} value={Util.roundNice(data[0].metric_tonnes_diverted, 2) + " MT"} icon="wsc:ghg" colour="#00BACD" subtitle={Util.roundNice(diversionPercent, 2) + "%"}/>
            </MetricStack>
        </View>;
    }

    const isMobile = Util.isMobile();

    return <ShinyBlockOuter minHeight={400} flexBasis={isMobile? "95%" : "49%"}>
        <ShinyHeading>
            <MetricStackTitle>Landfill Diversion</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <Tabular data={data} pageSize={pageSize.current}>
                <TabularColumn key="rank" data={(r) => r.rank} title="Rank" width={isMobile? null : 50}/>
                <TabularColumn key="name" data={(r) => r.name} title="Site" width={isMobile? null : 300}/>
                <TabularColumn key="name" data={(r) => Util.roundNice(r.metric_tonnes_waste, 2)} title="Landfill (MT)" width={isMobile? null : 70}/>
                <TabularColumn key="metric_tonnes_diverted" data={(r) => Util.roundNice(r.metric_tonnes_diverted, 2)} title="Diverted (MT)" width={isMobile? null : 70}/>
                <TabularColumn key="diversion_percent" data={(r) => Util.roundNice(r.diversion_percent, 2)} title="%" width={isMobile? null : 70}/>
            </Tabular>
        </ShinyBody>
    </ShinyBlockOuter>;
}

const formatChartDate = (d, customFormat) => {
    let format = "MMM";
    if(customFormat != undefined){
        format = customFormat;
    }

    if(Util.isMobile()){
        return moment(d).format(format);
    }
    return moment(d).format(format);
}

const GreenhouseGraph = ({portalData}) => {
    const [data, setData] = useState([]);

    useEffect(()=>{
        const newProduced = [];
        const newSaved = [];
        const MT_CO2_PER_MT_WASTE = 3.2414553472987;

        if(portalData.diversion != undefined && portalData.diversion.length > 0){
            _.forEach( portalData.diversion, (d)=>{
                const saved = -1 * d.recycled * MT_CO2_PER_MT_WASTE;
                const produced = d.waste * MT_CO2_PER_MT_WASTE;
                
                newProduced.push({label: formatChartDate(d.date), value: produced, legendLabel: d.nicePeriodName});
                newSaved.push({label: formatChartDate(d.date), value: saved, legendLabel: d.nicePeriodName});
            });       
        }

        const newData = [
            {
                label: "Produced",
                data: newProduced,
                svg: {fill: COLORS.grey},
                units: "MT CO₂"
            },
            {
                label: "Saved",
                data: newSaved,
                svg: {fill: COLORS.wsc_blue},
                units: "MT CO₂"
            }
        ];

        setData(newData);
    },[portalData]);

    const xAxisFormatLabel = (value, index) => {
        return value;
    }

    const yAxisFormatLabel = (value, index) => {
        let precision = 0;
        if(value < 10){
            precision = 2;
        } else if(value < 100){
            precision = 1;
        }   
        
        return " " + Util.roundNice(value, precision);
    }

    let graphHeight = 400;
    if(Util.isMobile()){
        graphHeight = 300;
    }

    return <ShinyBlockOuter minHeight={graphHeight} flexBasis={Util.isMobile()? "95%" : "49%"}>
        <ShinyHeading>
            <MetricStackTitle>Greenhouse Gas</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <BarChart 
                data={data}
                defaultDataPoint={data[0] != undefined? data[0].data[data[0].data.length-1] : undefined}
                yAccessor={ ({item}) => { return item.value; } }
                xAccessor={ ({item}) => { return item.label; } }
                xAxisFormatLabel={xAxisFormatLabel} 
                yAxisFormatLabel={yAxisFormatLabel}
                height={graphHeight}
                showGrid={true}
                showLabels={false}
                showLegend={true}
            />
        </ShinyBody>
    </ShinyBlockOuter>;
}

const Contributors = ({portalData, title = "Key Contributors"}) => {
    const [data, setData] = useState([]);
    const maxValue = useRef(0);
    const [units, setUnits] = useState("kg");

    useEffect(()=>{
        const newData = _.map(_.groupBy(portalData.materials, "name"), (materialGroup, materialName)=>{
            const sum = _.sumBy(materialGroup, "metric_tonnes");
            if(sum > maxValue.current){
                maxValue.current = sum;
            }

            return {
                label: materialName,
                value: sum,
                svg: {fill: materialGroup[0].main_colour}
            }
        });

        if(maxValue.current > 10){
            setUnits("MT");
        }

        setData(newData);
    },[portalData]);

    let graphHeight = 400;
    if(Util.isMobile()){
        graphHeight = 300;
    }

    const xAxisFormatLabel = (value, index) => {
        let precision = 0;
        /* if(value < 10){
            precision = 2;
        } else if(value < 100){
            precision = 1;
        } */

        if(index % 2 == 0){
            return Util.roundNice(value * (units == "kg"? 1000 : 1), precision);
        }

        return "";
    }

    const legendChildren = (data, hoveredBand) => {
        if(hoveredBand == null){
            return null;
        }
        
        return <View style={{flexDirection: "row"}}>
            <View style={{height: 20, flexBasis: 20, flexGrow: 0, backgroundColor: hoveredBand.svg.fill}}/> 
            <View style={{flexBasis: 10}}/> 
            <View >
                <StrongText>{hoveredBand.label}</StrongText> 
            </View>
            <View style={{flexBasis: 10}}/>
            <View >
                <StrongText>{Util.roundNice(hoveredBand.value * (units == "kg"? 1000 : 1), 2) + " " + units}</StrongText>
            </View>
        </View>;
    }

    const maxData = _.maxBy(data, "value");
    
    return <ShinyBlockOuter minHeight={graphHeight} flexBasis={Util.isMobile()? "95%" : "49%"}>
        <ShinyHeading>
            <MetricStackTitle>{title} ({portalData.dateRange.length || 0} Months)</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <BarChart
                data={data}
                height={graphHeight}
                horizontal={true}
                showGrid={true}
                showLegend={true}
                legendChildren={(data, hoveredBand)=>legendChildren(data, hoveredBand)}
                xAccessor={ ({item}) => { return item.label; } }
                xAxisAccessor={ ({item}) => { return item; }}
                xAxisScale={scale.scaleLinear}
                xAxisFormatLabel={xAxisFormatLabel}
                yAccessor={ ({item}) => { return item.value; } }
                yAxisAccessor={ ({item}) => { return item.label; }}
                yAxisScale={scale.scaleBand}
                defaultDataPoint={maxData}
                xAxisLabel={units == "kg"? "Kilograms" : "Metric Tonnes"}
            />
        </ShinyBody>
    </ShinyBlockOuter>;
}

const NewTabular = ({data = [], children = [], widths = []}) => {
    const [columns, setColumns] = useState([]);

    useEffect(()=>{
        doUpdate();
    }, [data, children]);

    const doUpdate = () => {
        const newColumns = {};
        const indexedColumnDefinitions = {};

        React.Children.forEach(children, (c, idx)=>{
            indexedColumnDefinitions[idx] = {...c.props};
        });

        _.forEach(_.keys(indexedColumnDefinitions), (k, idx)=>{
            const def = indexedColumnDefinitions[idx];

            if(def == undefined){
                //???
            } else {
                if(newColumns[idx] == undefined){
                    newColumns[idx] = [];

                    const titleCell = {
                        dataCalc: (d)=>def.title
                    }

                    newColumns[idx].push(titleCell);
                }
                _.forEach(data, (d)=>{
                    newColumns[idx].push({
                        dataCalc: def.data,
                        extraStyle: def.style || {}
                    });
                });                
            }
        });

        setColumns(_.values(newColumns));
    }

    return <View style={{flexDirection: "row", flexGrow: 1}}>
        {_.map(columns, (col, columnIdx)=>{
            return <View key={columnIdx} style={{flexDirection: "column", flexGrow: 1}}>
                {_.map(col, (cell, cellIdx)=>{
                    const cellNumber = parseInt(cellIdx);

                    return <BasicText key={cellNumber + "|" + columnIdx} style={{...{
                        backgroundColor: cellNumber % 2 == 1? "#f9f9f9" : null,
                        padding: 5,
                        fontWeight: cellNumber == 0? "bold" : "normal",
                        height: 30
                    }, ...cell.extraStyle}}>
                        {data != undefined && data[cellNumber == 0? cellNumber : cellNumber-1] != undefined ? cell.dataCalc(data[cellNumber == 0? cellNumber : cellNumber-1]) : null}
                    </BasicText>;
                })}
            </View>;
        })}
    </View>
}

const Composition12Month = ({portalData}) => {
    const [data, setData] = useState([]);
    const [cols, setCols] = useState([]);

    useEffect(()=>{
        if( !_.isEmpty(portalData) ){
            let monthlyCols = [];
            const materialColumn = <TabularColumn 
                key="material" 
                data={(row) =>{
                    return <View style={{flexDirection: "row", alignItems: "center"}}>
                        <ColorBlock color={row.main_colour} style={{marginHorizontal: 5}}/>
                        <BasicText>{row.name}</BasicText>
                    </View>
                }}
                title="Material class (MT)" 
                className="material-col"
            />;

            monthlyCols.push(materialColumn);
        
            const groups = _.groupBy(portalData.materials, (r)=>r.name);

            const monthlyWaste = _.map( groups, (group)=>{
                const row = _.pick( group[0], "name", "accent_colour", "main_colour", "density", "material_type" );
                
                _.each( group, (g)=>{
                    if(row[g.date] == undefined){
                        row[ g.date ] = g.metric_tonnes;
                    } else {
                        row[ g.date ] += g.metric_tonnes;
                    }
                });
                return row;
            });

            _.each( portalData.dateRange, (m)=>{
                const month = moment(m);
                const niceMonth = month.format('MMM YY');
                const className = niceMonth == moment().format('MMM YY') ? "current-month" : "";
                let highlight = false;

                if(niceMonth == moment().format('MMM YY')){
                    highlight = true;
                }

                const columnStyle = {};

                if(highlight == true){
                    columnStyle.backgroundColor = "rgba(203, 203, 203, 0.5)";
                }

                monthlyCols.push( <TabularColumn key={"month-" + month} data={ (row)=>Util.roundNice(row[month.format("YYYY-MM")], 2) } title={niceMonth} className={className} style={columnStyle} /> );
            });
    
            setData(monthlyWaste);
            setCols(monthlyCols);
        }
    }, [portalData]);

    return <ShinyBlockOuter flexBasis={"95%"}>
        <ShinyHeading>
            <MetricStackTitle>Composition History</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <ScrollView horizontal={true} contentContainerStyle={{flexDirection: "row", flexGrow: 1}}>
                <NewTabular data={data}>
                    {cols}
                </NewTabular>
            </ScrollView>
        </ShinyBody>
    </ShinyBlockOuter>;
}

const SustainabilityDashboard = ({portalData}) =>{
    let data = [];
    if(portalData.metrics != undefined){
        data = _.groupBy(_.orderBy(portalData.metrics["sustainability"], "periodTo", "asc"), "category");
    }

    return <PageWrapper>
        <Metrics data={data}/>
        <Diversion portalData={portalData} />
        <Contributors portalData={portalData} />
        <GreenhouseGraph portalData={portalData} />
        <Composition12Month portalData={portalData} />
    </PageWrapper>
};

export { Contributors, NewTabular };
export default SustainabilityDashboard;